import type { PerfilUsuario, Usuario } from '@/tipos';

/**
 * Hierarquia de perfis (espelha o backend em `shared/permissoes.ts`).
 * Maior número = maior nível de acesso.
 */
const HIERARQUIA: Record<PerfilUsuario, number> = {
  admin: 100,
  gestor: 80,
  diretor: 60,
  coordenador: 50,
  secretario: 40,
  professor: 20,
};

/**
 * Calcula o perfil efetivo (maior) entre uma lista de perfis.
 */
export function perfilEfetivo(perfis: PerfilUsuario[]): PerfilUsuario | null {
  if (!perfis.length) return null;
  return [...perfis].sort((a, b) => HIERARQUIA[b] - HIERARQUIA[a])[0];
}

/**
 * Verifica se uma permissão é atendida por um conjunto disponível.
 * Suporta wildcard '*' global e '<recurso>:*'.
 */
export function atende(disponiveis: string[], necessaria: string): boolean {
  if (disponiveis.includes('*')) return true;
  if (disponiveis.includes(necessaria)) return true;
  const [recurso] = necessaria.split(':');
  return disponiveis.includes(`${recurso}:*`);
}

/**
 * Permissões padrão concedidas implicitamente por cada perfil
 * (espelha PERMISSOES_POR_PERFIL do backend).
 */
// Espelha `PERMISSOES_POR_PERFIL` do backend (`shared/permissoes.ts`).
// Manter sincronizado — bugs de menu/UI quando divergem (ex: diretor sem
// "Comunicados" por faltar 'comunicado:*' aqui apesar de ter no backend).
export const PERMISSOES_POR_PERFIL: Record<PerfilUsuario, string[]> = {
  admin: ['*'],
  gestor: [
    'secretaria:listar',
    'secretaria:editar',
    'regional:*',
    'escola:*',
    'pessoa:*',
    'usuario:*',
    'profissional:*',
    'vinculo:*',
    'ano_letivo:listar',
    'ano_letivo:criar',
    'ano_letivo:editar',
    'periodo_letivo:listar',
    'turma:listar',
    'turma:encerrar',
    'disciplina:listar',
    'disciplina:criar',
    'disciplina:editar',
    'bncc:listar',
    'enturmacao:listar',
    'enturmacao:movimentar',
    'enturmacao:exceder_vagas',
    'transferencia:solicitar',
    'transferencia:liberar',
    'transferencia:listar',
    'horario:listar',
    'dashboard:visualizar',
    'dashboard:gestor',
    'configuracao:acessar',
    'observacao_aluno:*',
    'comunicado:*',
    'plano_aula:*',
    'consent_lgpd:*',
  ],
  diretor: [
    'escola:listar',
    'escola:editar',
    'pessoa:*',
    'usuario:listar',
    'usuario:criar',
    'usuario:editar',
    'usuario:redefinir_senha',
    'profissional:*',
    'vinculo:listar',
    'vinculo:criar',
    'vinculo:editar',
    'ano_letivo:listar',
    'ano_letivo:criar',
    'ano_letivo:editar',
    'periodo_letivo:*',
    'turma:*',
    'horario:*',
    'disciplina:listar',
    'aula:*',
    'frequencia:*',
    'avaliacao:*',
    'nota:*',
    'bncc:listar',
    'enturmacao:listar',
    'enturmacao:movimentar',
    'enturmacao:exceder_vagas',
    'transferencia:solicitar',
    'transferencia:liberar',
    'transferencia:listar',
    'dashboard:visualizar',
    'dashboard:diretor',
    'observacao_aluno:*',
    'comunicado:*',
    'plano_aula:*',
    'consent_lgpd:*',
  ],
  coordenador: [
    'escola:listar',
    'pessoa:listar',
    'vinculo:listar',
    'ano_letivo:listar',
    'periodo_letivo:listar',
    'turma:listar',
    'turma:criar',
    'turma:editar',
    'turma:encerrar',
    'disciplina:listar',
    'aula:*',
    'frequencia:*',
    'avaliacao:*',
    'nota:*',
    'bncc:listar',
    'enturmacao:listar',
    'enturmacao:movimentar',
    'transferencia:listar',
    'horario:listar',
    'dashboard:visualizar',
    'dashboard:diretor',
    'observacao_aluno:*',
    'comunicado:*',
    'plano_aula:*',
    'consent_lgpd:*',
  ],
  secretario: [
    'escola:listar',
    'pessoa:listar',
    'pessoa:criar',
    'pessoa:editar',
    'vinculo:listar',
    'periodo_letivo:listar',
    'turma:listar',
    'turma:encerrar',
    'usuario:listar',
    'bncc:listar',
    'enturmacao:listar',
    'enturmacao:movimentar',
    'transferencia:solicitar',
    'transferencia:liberar',
    'transferencia:listar',
    'horario:listar',
    'comunicado:*',
    'consent_lgpd:*',
  ],
  professor: [
    'pessoa:listar',
    'vinculo:listar',
    'periodo_letivo:listar',
    'turma:listar',
    'disciplina:listar',
    'aula:*',
    'frequencia:*',
    'avaliacao:listar',
    'avaliacao:criar',
    'nota:*',
    'comunicado:listar',
    'comunicado:criar',
    'bncc:listar',
    'enturmacao:listar',
    'horario:listar',
    'dashboard:professor',
    'observacao_aluno:*',
    'comunicado:*',
    'plano_aula:*',
    'consent_lgpd:*',
  ],
};

/**
 * Usuário de equipe: admin de plataforma (por permissão, sem vínculo)
 * ou profissional com vínculo ativo. Alunos/responsáveis retornam false.
 */
export function ehStaff(
  usuario: Pick<Usuario, 'vinculos' | 'permissoes' | 'perfil_efetivo'>
): boolean {
  if (usuario.perfil_efetivo === 'admin') return true;
  if (usuario.permissoes?.includes('admin') || usuario.permissoes?.includes('*')) return true;
  return (usuario.vinculos ?? []).some((v) => v.situacao === 'ativo');
}

export function permissoesEfetivas(
  perfil: PerfilUsuario | null | undefined,
  granulares: string[]
): string[] {
  const padrao = perfil ? PERMISSOES_POR_PERFIL[perfil] : [];
  return Array.from(new Set([...padrao, ...granulares]));
}

export const PERFIS_LABELS: Record<PerfilUsuario, string> = {
  admin: 'Administrador',
  gestor: 'Gestor',
  diretor: 'Diretor',
  coordenador: 'Coordenador',
  secretario: 'Secretário',
  professor: 'Professor',
};
